// A PARTY'S BALANCE AS ROWS, NOT A SENTENCE (his "Amount, chain, date and time everything in 1st column- doesn't it look
// wrong" and "check everything where it applies", 1 Nov 2026; the audit's #6): the forms said "AED · owes you 120,000 →
// you hold 5,000" in small grey text under the party box. One fact per place instead — the words at the left, the figure
// mono at the right with its unit muted after it, body size; before → after as two aligned rows, "Now" and "After".
//
//   Now     owes you   120,000 AED
//   After   you hold     5,000 AED
//
// The words and the dust rule are BoxLines' (boxParts): positive = "owes you", negative = "you hold", under dust =
// "square" — no sign printed; the one negative token (`text-neg`) underlines a "you hold" as BoxWords does.
import { boxParts } from '../../BoxLines';
import { displayAsset, fmtAmount, fmtRate } from '../../../lib/format';
import { foldAsset } from '../../../lib/naming';

export interface BalanceLine {
  asset: string;
  now: number;
  /** The balance once this entry posts. Omit to show the balance as it stands. */
  after?: number;
}

function Cells({ value, asset }: { value: number; asset: string }) {
  const { words, figure } = boxParts(value, asset);
  // Square still names its asset (1 Nov 2026 audit: an unlabelled "square" above a USDT row).
  if (!figure) return <><span className="text-tg-hint">square</span><span className="text-right text-meta text-tg-hint">{displayAsset(foldAsset(asset))}</span></>;
  return (
    <>
      <span className="text-tg-hint">{words}</span>
      <span className={`whitespace-nowrap text-right ${value < 0 ? 'text-neg' : ''}`}>
        <span className="font-mono tabular">{figure}</span>{' '}<span className="text-meta text-tg-hint">{displayAsset(foldAsset(asset))}</span>
      </span>
    </>
  );
}

/** The rows. A line with `after` draws Now and After; lines without draw one row each. `label` heads the block when the
 *  field above does not already say whose balance it is. */
export function BalanceRows({ lines, label, className = '' }: { lines: ReadonlyArray<BalanceLine>; label?: string; className?: string }) {
  const changes = lines.some((l) => l.after !== undefined);
  return (
    <div className={`mt-2 text-body ${className}`}>
      {label && <p className="mb-1 text-meta text-tg-hint">{label}</p>}
      <div className={`grid items-baseline gap-x-3 gap-y-1 ${changes ? 'grid-cols-[auto_minmax(0,1fr)_auto]' : 'grid-cols-[minmax(0,1fr)_auto]'}`}>
        {lines.map((l) => (
          l.after !== undefined ? (
            <span key={l.asset} className="contents">
              <span className="text-meta text-tg-hint">Now</span><Cells value={l.now} asset={l.asset} />
              <span className="text-meta text-tg-hint">After</span><Cells value={l.after} asset={l.asset} />
            </span>
          ) : (
            <span key={l.asset} className="contents">
              {changes && <span className="text-meta text-tg-hint">Now</span>}
              <Cells value={l.now} asset={l.asset} />
            </span>
          )
        ))}
      </div>
    </div>
  );
}

/** A plain labelled figure row — the same face for a fact that is not a party's balance (an account's balance under its
 *  picker, the cheque's total / deposit / balance). */
export function FigureRow({ label, value, asset, strong = false }: { label: string; value: number | null; asset: string; strong?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-body">
      <span className="text-tg-hint">{label}</span>
      {value === null ? <span className="text-tg-hint">—</span> : (
        <span className="whitespace-nowrap text-right">
          <span className={`font-mono tabular ${strong ? 'font-semibold' : ''}`}>{fmtAmount(value, asset)}</span>{' '}
          <span className="text-meta text-tg-hint">{displayAsset(asset)}</span>
        </span>
      )}
    </div>
  );
}

/** A rate on its own labelled line (`approx`: a worked-out inverse, said with ≈ — 1 ÷ 3.67 is not exactly 0.2725) — "1 USDT = 3.6725 AED" — the inverse as a second, quieter one (never two equations in
 *  one cell). Rates are never rounded by rule 10 (N-090): fmtRate keeps their places. */
export function RateRow({ label, base, quote, rate, quiet = false, approx = false }: { label: string; base: string; quote: string; rate: number; quiet?: boolean; approx?: boolean }) {
  return (
    <div className={`flex items-baseline justify-between gap-3 ${quiet ? 'text-meta text-tg-hint' : 'text-body'}`}>
      <span className="text-tg-hint">{label}</span>
      <span className="whitespace-nowrap text-right">
        1 {displayAsset(base)} {approx ? '≈' : '='} <span className="font-mono tabular">{fmtRate(rate, quote)}</span>{'\u00a0'}{displayAsset(quote)}
      </span>
    </div>
  );
}
